import { Component, type ErrorInfo, type ReactNode } from "react";
import { MIN_DESKTOP_WIDTH } from "@/config/designer";

export function MobileNotice(): React.ReactElement {
  return (
    <main className="fullscreen-message">
      <div className="fullscreen-message__inner">
        <p className="eyebrow">ETHOS Climbing</p>
        <h1>ETHOS Hold Shaper V1.1 works best on a desktop or laptop</h1>
        <p>
          Shaping foam needs a pointer and a bit of screen. Open this page on a computer with a
          window at least {MIN_DESKTOP_WIDTH} px wide and you will be carving in seconds.
        </p>
        <p className="muted">Tablet support is coming later.</p>
      </div>
    </main>
  );
}

export function WebglNotice(): React.ReactElement {
  return (
    <main className="fullscreen-message">
      <div className="fullscreen-message__inner">
        <p className="eyebrow">ETHOS Climbing</p>
        <h1>This browser cannot show 3D</h1>
        <p>
          ETHOS Hold Shaper V1.1 needs WebGL, which appears to be unavailable or switched off here.
        </p>
        <p className="muted">
          Try the latest Chrome, Edge, Firefox or Safari, and check that hardware acceleration is
          enabled.
        </p>
      </div>
    </main>
  );
}

export function Loader({ label }: { label: string }): React.ReactElement {
  return (
    <div className="loader" role="status" aria-live="polite">
      <div className="loader__bar">
        <span />
      </div>
      <p className="muted">{label}</p>
    </div>
  );
}

interface BoundaryState {
  error: Error | null;
}

/**
 * Nothing in a modelling tool should be able to strand the user. If a render
 * throws, we show the error and offer a way back to a working state rather
 * than a blank page.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ethos] unrecoverable UI error", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <main className="fullscreen-message">
        <div className="fullscreen-message__inner">
          <p className="eyebrow">Something broke</p>
          <h1>The designer hit an error</h1>
          <p>{error.message}</p>
          <p className="muted">
            Your last autosave is still on this machine. Reloading will offer to continue it.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 18 }}>
            <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => this.setState({ error: null })}
            >
              Try to continue
            </button>
          </div>
        </div>
      </main>
    );
  }
}

/** Cheap, side-effect-free WebGL probe. */
export function detectWebgl(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const context =
      canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl");
    if (!context) return false;
    const lose = (context as WebGLRenderingContext).getExtension("WEBGL_lose_context");
    lose?.loseContext();
    return true;
  } catch {
    return false;
  }
}
